// lib/api.ts

import {
  ActionItemCreate,
  ActionItemDetail,
  ActionItemList,
  ActionItemResponse,
  ActionItemStatusUpdate,
  ActionItemUpdate,
  APCMStatsResponse,
  CallCreateList,
  CampaignEnrollmentResponse,
  CallCreateRequest,
  CallList,
  CallLog,
  CallResponse,
  Calls,
  CallTranscript,
  CarePlanCreate,
  CarePlanResponse,
  CarePlanNote,
  CarePlansAndHealthSummariesResponse,
  HealthSummaryResponse,
  MainSystemPrompt,
  MainSystemPromptCreate,
  MedicalInfoResponse,
  MultipleCallsScheduleCreate,
  BatchConfig,
  OrganizationCreate,
  OrganizationWithUsers,
  ReportFrequency,
  ReportSettings,
  ReportSubscription,
  OutboundCallRequest,
  PatientAPCMConsentCreate,
  PatientAPCMConsentResponse,
  PatientCreate,
  PatientIngestCSVResponse,
  PatientOrganizationCreate,
  PatientOrganizationResponse,
  PatientResponse,
  PatientUpdate,
  PostCallWorkflowOutputs,
  StrategyCreate,
  StrategyUpdate,
  StrategyVersion,
  StrategyVersionCreate,
  StrategyWithVersions,
  TokenCreate,
  TokenList,
  TokenResponse,
  TokenUpdate,
  UpcomingCall,
  User,
  VerifiedPhoneNumber,
  PatientCommunicationHistoryResponse,
} from "@/lib/types";
import { CallFilterResult, CallQueryResults } from "@/types/callFilters";
import { applyAuthHeaders } from "@/lib/authHeaders";
import { ImpersonationTarget } from "@/stores/impersonationStore";
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { DEFAULT_CHAT_MODEL } from "@/lib/llm";
import type {
  APCMCallResponse,
  MultipleAPCMCallsResponse,
  OutboundCallResponse,
} from "@/lib/apcmCall";

export interface ChatCompletionOptions {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  /** Anthropic tool input_schema; forces structured JSON output. */
  jsonSchema?: Record<string, unknown>;
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    await applyAuthHeaders(config);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Exported so feature modules can reuse the authenticated axios instance
// instead of standing up another copy of the interceptor boilerplate.
export async function apiCall<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await axiosInstance(config);
    return response.data;
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
}

async function streamingApiCall(
  config: AxiosRequestConfig,
  onChunk: (chunk: string) => void
): Promise<string> {
  let buffer = "";

  try {
    const response = await axiosInstance({
      ...config,
      responseType: "text",
      headers: {
        ...config.headers,
        Accept: "text/event-stream",
      },
      onDownloadProgress: (progressEvent: any) => {
        // Access the XMLHttpRequest directly through currentTarget
        const xhr = progressEvent.event.currentTarget;
        if (!xhr || !xhr.responseText) return;

        // Get only the new part of the response
        const newText = xhr.responseText.substring(buffer.length);
        buffer = xhr.responseText;

        if (!newText) return;

        // Process each line of the new text
        const lines = newText.split("\n");
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          // Remove "data: " prefix if it exists
          const cleanedChunk = trimmed.startsWith("data:")
            ? trimmed.substring(6)
            : trimmed;

          onChunk(cleanedChunk);
        }
      },
    });

    // Build complete text from the full response data (onDownloadProgress may miss final chunks)
    let fullText = "";
    const rawText = typeof response.data === "string" ? response.data : "";
    for (const line of rawText.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      fullText += trimmed.startsWith("data:") ? trimmed.substring(6) : trimmed;
    }

    console.log("Stream complete");
    return fullText;
  } catch (error) {
    console.error("Streaming API call failed:", error);
    throw error;
  }
}

export const api = {
  // Dial an existing PENDING call now. Needs APCM ops access; 409 when the call policy refuses.
  outboundCall: (data: OutboundCallRequest) =>
    apiCall<OutboundCallResponse>({
      url: "/daily/outbound_call",
      method: "POST",
      data,
    }),

  // Health
  healthCheck: () =>
    apiCall<string>({
      url: "/health",
      method: "GET",
    }),

  secretCheck: () =>
    apiCall<string>({
      url: "/health/secret-check",
      method: "GET",
    }),

  // User
  getOrCreateUser: () =>
    apiCall<User>({
      url: "/user",
      method: "POST",
    }),

  getUser: () =>
    apiCall<User>({
      url: "/user/",
      method: "GET",
    }),

  // Impersonation ("view as user"). Superuser only; the API authorizes against
  // the bearer token, never the header.
  listImpersonationTargets: () =>
    apiCall<ImpersonationTarget[]>({
      url: "/impersonation/targets",
      method: "GET",
    }),

  startImpersonation: (targetUserId: string) =>
    apiCall<User>({
      url: "/impersonation/start",
      method: "POST",
      data: { target_user_id: targetUserId },
    }),

  stopImpersonation: () =>
    apiCall<{ closed: number }>({
      url: "/impersonation/stop",
      method: "POST",
    }),

  getWorkflowRoles: (organizationId: string) => apiCall<WorkflowRoleAssignment[]>({url:`/organization/${organizationId}/workflow-roles`,method:'GET'}),
  setWorkflowRole: (organizationId: string, role: string, data: {user_id:string|null;expected_revision:number}) => apiCall<WorkflowRoleAssignment[]>({url:`/organization/${organizationId}/workflow-roles/${encodeURIComponent(role)}`,method:'PUT',data}),

  // Organization
  listOrganizations: () =>
    apiCall<OrganizationWithUsers[]>({
      url: "/organization",
      method: "GET",
    }),

  createOrganization: (data: OrganizationCreate) =>
    apiCall<OrganizationWithUsers>({
      url: "/organization",
      method: "POST",
      data,
    }),

  addUserToOrganization: (organizationId: string, userEmail: string, role: "admin" | "user" | "staff" = "user") =>
    apiCall<OrganizationWithUsers>({
      url: `/organization/${organizationId}/add-user`,
      method: "POST",
      params: { user_email: userEmail, role },
    }),

  inviteUserToOrganization: (
    organizationId: string,
    invite: {
      email: string;
      name?: string;
      role?: "admin" | "user" | "staff";
      is_superuser?: boolean;
    }
  ) =>
    apiCall<OrganizationWithUsers>({
      url: `/organization/${organizationId}/invite-user`,
      method: "POST",
      data: invite,
    }),

  getOrganization: (organizationId: string) =>
    apiCall<OrganizationWithUsers>({
      url: `/organization/${organizationId}`,
      method: "GET",
    }),

  updateOrganization: (organizationId: string, data: any) =>
    apiCall<OrganizationWithUsers>({
      url: `/organization/${organizationId}`,
      method: "PATCH",
      data,
    }),

  // Emailed activity reports
  getReportSubscriptions: (organizationId: string) =>
    apiCall<ReportSettings>({
      url: `/organization/${organizationId}/reports/subscriptions`,
      method: "GET",
    }),

  createReportSubscription: (
    organizationId: string,
    data: { email: string; frequency: ReportFrequency; is_active?: boolean }
  ) =>
    apiCall<ReportSubscription>({
      url: `/organization/${organizationId}/reports/subscriptions`,
      method: "POST",
      data,
    }),

  updateReportSubscription: (
    organizationId: string,
    subscriptionId: string,
    data: { frequency?: ReportFrequency; is_active?: boolean }
  ) =>
    apiCall<ReportSubscription>({
      url: `/organization/${organizationId}/reports/subscriptions/${subscriptionId}`,
      method: "PATCH",
      data,
    }),

  deleteReportSubscription: (organizationId: string, subscriptionId: string) =>
    apiCall<void>({
      url: `/organization/${organizationId}/reports/subscriptions/${subscriptionId}`,
      method: "DELETE",
    }),

  sendTestReport: (
    organizationId: string,
    data: { frequency: ReportFrequency; email?: string }
  ) =>
    apiCall<{ sent_to: string; period: string; timezone: string }>({
      url: `/organization/${organizationId}/reports/send-test`,
      method: "POST",
      data,
    }),

  reportPreviewUrl: (organizationId: string, frequency: ReportFrequency) =>
    `/organization/${organizationId}/reports/preview?frequency=${frequency}`,

  setOrganizationDefaultPhoneNumber: (
    organizationId: string,
    verifiedPhoneNumberId: string
  ) =>
    apiCall<VerifiedPhoneNumber[]>({
      url: `/organization/${organizationId}/default-phone-number`,
      method: "PATCH",
      params: { verified_phone_number_id: verifiedPhoneNumberId },
    }),

  updateOrganizationAPCMStrategy: (
    organizationId: string,
    strategyVersionId: string
  ) =>
    apiCall<OrganizationWithUsers>({
      url: `/organization/${organizationId}`,
      method: "PATCH",
      data: { default_apcm_strategy_version_id: strategyVersionId },
    }),

  getOrganizationCalls: (organizationId: string, strategyId?: string) =>
    apiCall<CallList>({
      url: `/organization/${organizationId}/calls`,
      method: "GET",
      params: { strategy_id: strategyId },
    }),

  verifyPhoneNumber: (id: string, phoneNumber: string) =>
    apiCall<VerifiedPhoneNumber>({
      url: `/organization/${id}/verify-phone`,
      method: "POST",
      params: { phone_number: phoneNumber },
    }),

  getVerifiedPhoneNumbers: (organizationId: string) =>
    apiCall<VerifiedPhoneNumber[]>({
      url: `/organization/${organizationId}/verified-phone-numbers`,
      method: "GET",
    }),

  getAPCMStats: (organizationId: string) =>
    apiCall<APCMStatsResponse>({
      url: `/organization/${organizationId}/apcm-stats`,
      method: "GET",
    }),

  getUpcomingCalls: (organizationId: string) =>
    apiCall<UpcomingCall[]>({
      url: `/organization/${organizationId}/upcoming-calls/v3`,
      method: "GET",
    }),

  // Strategy
  getStrategy: (id: string) =>
    apiCall<StrategyWithVersions>({
      url: `/strategy/${id}`,
      method: "GET",
    }),

  updateStrategy: (id: string, data: StrategyUpdate) =>
    apiCall<StrategyWithVersions>({
      url: `/strategy/${id}`,
      method: "PUT",
      data,
    }),

  getOrganizationStrategies: (orgId: string, skip = 0, limit = 100) =>
    apiCall<StrategyWithVersions[]>({
      url: `/strategy/organization/${orgId}/strategies`,
      method: "GET",
      params: { skip, limit },
    }),

  createStrategy: (data: StrategyCreate) =>
    apiCall<StrategyWithVersions>({
      url: "/strategy",
      method: "POST",
      data,
    }),

  createStrategyVersion: (id: string, data: StrategyVersionCreate) =>
    apiCall<StrategyVersion>({
      url: `/strategy/${id}/version`,
      method: "POST",
      data,
    }),

  // Strategy Version
  getStrategyVersion: (id: string) =>
    apiCall<StrategyVersion>({
      url: `/strategy-version/${id}`,
      method: "GET",
    }),

  // Main System Prompt
  getLatestMainSystemPrompt: () =>
    apiCall<MainSystemPrompt>({
      url: "/main-system-prompt",
      method: "GET",
    }),

  createMainSystemPrompt: (data: MainSystemPromptCreate) =>
    apiCall<MainSystemPrompt>({
      url: "/main-system-prompt",
      method: "POST",
      data,
    }),

  getMainSystemPrompt: (id: string) =>
    apiCall<MainSystemPrompt>({
      url: `/main-system-prompt/${id}`,
      method: "GET",
    }),

  getAllMainSystemPrompts: () =>
    apiCall<MainSystemPrompt[]>({
      url: "/main-system-prompt/all",
      method: "GET",
    }),

  // Call
  createCall: (data: CallCreateRequest) =>
    apiCall<CallResponse>({
      url: "/call",
      method: "POST",
      data,
    }),

  createBulkCalls: (data: CallCreateList) =>
    apiCall<Calls>({
      url: "/call/multiple",
      method: "POST",
      data,
    }),

  getCall: (id: string) =>
    apiCall<CallResponse>({
      url: `/call/${id}`,
      method: "GET",
    }),

  startCall: (id: string) =>
    apiCall<string>({
      url: `/call/${id}/start`,
      method: "POST",
    }),

  cloneCall: (id: string) =>
    apiCall<CallResponse>({
      url: `/call/${id}/clone`,
      method: "POST",
    }),

  getCallRecording: async (id: string): Promise<Blob> => {
    const response = await axiosInstance.get<Blob>(`/call/${id}/recording`, {
      responseType: "blob",
    });
    return response.data;
  },

  getCallLog: (id: string) =>
    apiCall<CallLog>({
      url: `/call/${id}/log`,
      method: "GET",
    }),

  // Root
  root: () =>
    apiCall<string>({
      url: "/hi",
      method: "GET",
    }),
  getCallTranscript: (id: string) =>
    apiCall<CallTranscript>({
      url: `/call/${id}/transcription`,
      method: "GET",
    }),

  getPostCallWorkflowOutputs: (id: string) =>
    apiCall<PostCallWorkflowOutputs>({
      url: `/call/${id}/post-call-workflow/outputs`,
      method: "GET",
    }),
  streamChatCompletion: (
    messages: any[],
    onChunk: (chunk: string) => void,
    options?: ChatCompletionOptions
  ) =>
    streamingApiCall(
      {
        url: "/chat/completions",
        method: "POST",
        data: {
          messages,
          model: options?.model ?? DEFAULT_CHAT_MODEL,
          stream: true,
          temperature: options?.temperature ?? 0.7,
          ...(options?.maxTokens !== undefined
            ? { max_tokens: options.maxTokens }
            : {}),
          // When provided, the backend forces Claude to emit an object matching
          // this schema (via tool use), so the response is always valid JSON.
          ...(options?.jsonSchema ? { json_schema: options.jsonSchema } : {}),
        },
      },
      onChunk
    ),

  // Settings
  generateToken: (data: TokenCreate) =>
    apiCall<TokenResponse>({
      url: "/token/",
      method: "POST",
      data,
    }),

  revokeToken: (id: string) =>
    apiCall<TokenResponse>({
      url: `/token/revoke/${id}`,
      method: "DELETE",
    }),

  listTokens: (organizationId: string | null) =>
    apiCall<TokenList>({
      url: `/token/list`,
      method: "GET",
      params: { organization_id: organizationId },
    }),

  updateToken: (id: string, data: TokenUpdate) =>
    apiCall<TokenResponse>({
      url: `/token/${id}`,
      method: "PATCH",
      data,
    }),

  // Patient endpoints
  createPatient: (data: PatientCreate) =>
    apiCall<PatientResponse>({
      url: "/patient",
      method: "POST",
      data,
    }),

  getPatient: (id: string) =>
    apiCall<PatientResponse>({
      url: `/patient/${id}`,
      method: "GET",
    }),

  updatePatient: (id: string, data: PatientUpdate) =>
    apiCall<PatientResponse>({
      url: `/patient/${id}`,
      method: "PATCH",
      data,
    }),

  getPatientCommunicationHistory: (
    patientId: string,
    params?: {
      type_filter?: "call" | "sms" | "all";
      limit?: number;
      offset?: number;
    }
  ) =>
    apiCall<PatientCommunicationHistoryResponse>({
      url: `/patient/${patientId}/communication-history`,
      method: "GET",
      params,
    }),

  addPatientToOrganization: (data: PatientOrganizationCreate) =>
    apiCall<PatientOrganizationResponse>({
      url: "/patient/organization",
      method: "POST",
      data,
    }),

  getOrganizationPatients: (organizationId: string) =>
    apiCall<PatientResponse[]>({
      url: `/patient/organization/${organizationId}`,
      method: "GET",
    }),

  getPatientsAdvancedV3: (
    organizationId: string,
    params: {
      // Patient Demographics
      patient_name?: string;
      patient_identifier?: string; // MRN / external id or FHIR id (substring)
      patient_phone?: string;
      patient_gender?: string;
      patient_age_min?: number;
      patient_age_max?: number;
      include_tags?: string;
      exclude_tags?: string;

      // Call Status & History
      call_statuses?: string;
      call_types?: string;
      call_date_start?: string;
      call_date_end?: string;
      has_calls?: boolean;

      // Call Outcome Analysis
      latest_call_status?: string;
      call_attempts_min?: number;
      call_attempts_max?: number;
      never_answered?: boolean;

      // Consent Management
      consent_status?: string;
      consent_method?: string;
      needs_consent?: boolean;

      // Time-based Filters
      last_contact_start?: string;
      last_contact_end?: string;
      due_for_contact?: boolean;
      overdue_contact?: boolean;

      // Care Management
      care_plan_status?: string;
      has_active_care_plan?: boolean;
      scheduled_calls_pending?: boolean;

      // Complex Logical Filters
      call_success_rate_min?: number;
      call_success_rate_max?: number;
      response_pattern?: string;
      priority_level?: string;

      // Schedule-based Filters
      scheduled_after?: string;
      scheduled_before?: string;
      scheduled_today?: boolean;
      scheduled_tomorrow?: boolean;
      scheduled_this_week?: boolean;

      // Negative filtering for calls
      no_calls_start?: string;
      no_calls_end?: string;
      no_call_types?: string;
      no_call_types_start?: string;
      no_call_types_end?: string;

      // Pagination & Sorting
      page?: number;
      page_size?: number;
      sort_by?: string;
      sort_order?: string;
    }
  ) =>
    apiCall<{
      results: any[]; // Using any for now as the response type might be different from PatientResponse
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
      query_summary: {
        applied_filters: Record<string, any>;
        patient_count: number;
        avg_success_rate: number;
        consent_pending_count: number;
        overdue_count: number;
      };
    }>({
      url: `/patient/organization/${organizationId}/v3`,
      method: "GET",
      params,
    }),

  ingestPatientFiles: (organizationId: string, formData: FormData) =>
    apiCall({
      url: `/patient/ingest?organization_id=${organizationId}`,
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

  ingestPatientFilesFromCSV: (organizationId: string, formData: FormData) =>
    apiCall<PatientIngestCSVResponse>({
      url: "/patient/ingest/from-csv",
      method: "POST",
      params: { organization_id: organizationId },
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

  ingestMultiplePatientFiles: (organizationId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    return apiCall<{ message: string }>({
      url: "/patient/ingest/multiple-html",
      method: "POST",
      params: { organization_id: organizationId },
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  ingestPatientFilesFromGoogleDrive: (
    organizationId: string,
    folderId: string,
    recursive: boolean = false
  ) =>
    apiCall<any>({
      url: "/patient/ingest/from-google-drive",
      method: "POST",
      params: {
        organization_id: organizationId,
        folder_id: folderId,
        recursive: recursive,
      },
    }),

  generatePatientPlanOfCare: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    return apiCall<string>({
      url: "/patient/generate-plan",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
    });
  },

  // Patient consent endpoints
  createPatientConsent: (data: {
    patient_id: string;
    organization_id: string;
    consent_status: string;
    method_of_consent?: string | null;
    document_url?: string | null;
    call_id?: string | null;
  }) =>
    apiCall<PatientAPCMConsentResponse>({
      url: "/patient-consent",
      method: "POST",
      data,
    }),

  getPatientConsent: (consentId: string) =>
    apiCall<PatientAPCMConsentResponse>({
      url: `/patient-consent/${consentId}`,
      method: "GET",
    }),

  getOrganizationConsents: (organizationId: string) =>
    apiCall<PatientAPCMConsentResponse[]>({
      url: `/patient-consent/organization/${organizationId}`,
      method: "GET",
    }),

  getPatientOrganizationConsents: (organizationId: string, patientId: string) =>
    apiCall<PatientAPCMConsentResponse[]>({
      url: `/patient-consent/organization/${organizationId}/patient/${patientId}`,
      method: "GET",
    }),

  // Call now, through the call policy: 409 with `detail` when refused, 403 without APCM ops access.
  makeAPCMCall: async (
    patientId: string,
    organizationId: string,
    apcmStrategyVersionId: string,
    verifiedPhoneNumberId: string
  ): Promise<APCMCallResponse> => {
    const response = await axiosInstance.post(
      `/patient/make-apcm-call`,
      {}, // empty body
      {
        params: {
          // send as query parameters
          patient_id: patientId,
          organization_id: organizationId,
          apcm_strategy_version_id: apcmStrategyVersionId,
          verified_phone_number_id: verifiedPhoneNumberId,
        },
      }
    );
    return response.data;
  },

  makeMultipleAPCMCalls: async (
    patientIds: string[],
    organizationId: string,
    apcmStrategyVersionId: string,
    verifiedPhoneNumberId: string
  ): Promise<MultipleAPCMCallsResponse> => {
    return apiCall<MultipleAPCMCallsResponse>({
      url: "/patient/multiple/apcm-calls",
      method: "POST",
      params: {
        organization_id: organizationId,
        apcm_strategy_version_id: apcmStrategyVersionId,
        verified_phone_number_id: verifiedPhoneNumberId,
      },
      data: patientIds,
    });
  },

  addTagsToMultiplePatients: (patientIds: string[], tags: string[]) =>
    apiCall<string>({
      url: "/patient/multiple/tags",
      method: "POST",
      data: {
        patients: patientIds,
        tags,
      },
    }),

  scheduleMultipleAPCMConsentCalls: async (
    patientIds: string[],
    organizationId: string,
    apcmStrategyVersionId: string,
    verifiedPhoneNumberId: string,
    schedulingInfo: MultipleCallsScheduleCreate
  ): Promise<any> => {
    return apiCall<any>({
      url: "/patient/multiple/schedule-apcm-consent-calls",
      method: "POST",
      params: {
        organization_id: organizationId,
        apcm_strategy_version_id: apcmStrategyVersionId,
        verified_phone_number_id: verifiedPhoneNumberId,
      },
      data: {
        patients: patientIds,
        scheduling_info: schedulingInfo,
      },
    });
  },

  scheduleCallsInBatches: async (
    patientIds: string[],
    organizationId: string,
    apcmStrategyVersionId: string,
    verifiedPhoneNumberId: string,
    startDateTime: string,
    batchConfig: BatchConfig
  ): Promise<any> => {
    return apiCall<any>({
      url: "/patient/multiple/schedule-calls-in-batches",
      method: "POST",
      params: {
        organization_id: organizationId,
        apcm_strategy_version_id: apcmStrategyVersionId,
        verified_phone_number_id: verifiedPhoneNumberId,
        start_datetime: startDateTime,
      },
      data: {
        patients: patientIds,
        batch_config: batchConfig,
      },
    });
  },

  scheduleMultipleAPCMCheckinCalls: async (
    patientIds: string[],
    organizationId: string,
    apcmCheckingStrategyVersionId: string,
    verifiedPhoneNumberId: string,
    schedulingInfo: {
      schedule_datetime: string;
      timezone_name?: string;
      flexible_time_window?: boolean;
    }
  ): Promise<{
    message: string;
    patient_count: number;
  }> => {
    return apiCall<{
      message: string;
      patient_count: number;
    }>({
      url: "/patient/apcm-checkin-call/schedule/multiple",
      method: "POST",
      params: {
        organization_id: organizationId,
        apcm_checking_strategy_version_id: apcmCheckingStrategyVersionId,
        verified_phone_number_id: verifiedPhoneNumberId,
      },
      data: {
        patient_ids: patientIds,
        scheduling_info: schedulingInfo,
      },
    });
  },

  updateMultiplePatientsLanguage: async (
    patientIds: string[],
    language: string
  ): Promise<any> => {
    return apiCall<any>({
      url: `/patient/bulk/language?language=${encodeURIComponent(language)}`,
      method: "PATCH",
      data: patientIds,
    });
  },

  // Action Item endpoints
  getOrganizationActionItems: (organizationId: string) =>
    apiCall<ActionItemList>({
      url: `/api/action-item/organization/${organizationId}`,
      method: "GET",
    }),

  createActionItem: (data: ActionItemCreate) =>
    apiCall<ActionItemResponse>({
      url: "/api/action-item",
      method: "POST",
      data,
    }),

  getActionItem: (actionItemId: string) =>
    apiCall<ActionItemDetail>({
      url: `/api/action-item/${actionItemId}`,
      method: "GET",
    }),

  getPatientActionItems: (patientId: string, organizationId: string) =>
    apiCall<ActionItemList>({
      url: `/api/action-item/patient/${patientId}/organization/${organizationId}`,
      method: "GET",
    }),

  updateActionItem: (actionItemId: string, data: ActionItemUpdate) =>
    apiCall<ActionItemResponse>({
      url: `/api/action-item/${actionItemId}`,
      method: "PUT",
      data,
    }),

  updateActionItemStatus: (
    actionItemId: string,
    data: ActionItemStatusUpdate
  ) =>
    apiCall<ActionItemResponse>({
      url: `/api/action-item/${actionItemId}/status`,
      method: "PUT",
      data,
    }),

  // Care Plan endpoints
  getPatientCarePlans: (patientId: string, organizationId: string) =>
    apiCall<CarePlanResponse[]>({
      url: `/patient/${patientId}/care-plans`,
      method: "GET",
      params: { organization_id: organizationId },
    }),

  getCarePlansAndHealthSummaries: (patientId: string, organizationId: string) =>
    apiCall<CarePlansAndHealthSummariesResponse>({
      url: `/patient/v2/${patientId}/care-plans-and-health-summaries`,
      method: "GET",
      params: { organization_id: organizationId },
    }),

  uploadCarePlan: (patientId: string, data: CarePlanCreate) =>
    apiCall<CarePlanResponse[]>({
      url: `/patient/${patientId}/care-plan`,
      method: "POST",
      data,
    }),

  updateCarePlan: (carePlanId: string, carePlanDetails: object) =>
    apiCall<CarePlanResponse>({
      url: `/patient/care-plan/${carePlanId}`,
      method: "PUT",
      data: { care_plan_details: carePlanDetails },
    }),

  getCarePlanNote: (carePlanId: string, includeHeader: boolean = true) =>
    apiCall<CarePlanNote>({
      url: `/patient/care-plan/${carePlanId}/note`,
      method: "GET",
      params: { include_header: includeHeader },
    }),

  updateCarePlanStatus: (carePlanId: string, carePlanStatus: string) =>
    apiCall<CarePlanResponse[]>({
      url: `/patient/care-plan/${carePlanId}/status`,
      method: "PATCH",
      params: { care_plan_status: carePlanStatus },
    }),

  ingestCarePlan: (organizationId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    return apiCall<any>({
      url: "/patient/care-plan/ingest",
      method: "POST",
      params: { organization_id: organizationId },
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  downloadMultipleCarePlanPDFs: async (
    patientIds: string[],
    organizationId: string
  ): Promise<Blob> => {
    const response = await axiosInstance.post<Blob>(
      `/patient/care-plans/download-multiple-pdfs`,
      patientIds,
      {
        params: { organization_id: organizationId },
        responseType: "blob",
      }
    );
    return response.data;
  },

  generateMissingCarePlanPDFs: (organizationId?: string, limit?: number) =>
    apiCall<string>({
      url: "/patient/care-plans/generate-missing-pdfs",
      method: "POST",
      params: {
        organization_id: organizationId,
        limit: limit,
      },
    }),

  // Medical Info endpoint
  getMedicalInfo: (patientId: string, organizationId: string) =>
    apiCall<MedicalInfoResponse[]>({
      url: `/patient/${patientId}/medical-info`,
      method: "GET",
      params: { organization_id: organizationId },
    }),

  // Delete multiple scheduled calls
  deleteMultipleSchedules: (callIds: string[]) =>
    apiCall<any>({
      url: "/call/schedules/multiple",
      method: "DELETE",
      data: callIds,
    }),

  // Query calls with advanced filtering
  // Campaigns
  getCampaignsWithStats: (params?: { status?: string }) =>
    apiCall<any[]>({
      url: `/campaigns`,
      method: "GET",
      params,
    }),

  createCampaign: (data: { name: string; workflow_type: string; config: Record<string, any> }) =>
    apiCall<any>({
      url: `/campaigns`,
      method: "POST",
      data,
    }),

  deleteCampaign: (campaignId: string) =>
    apiCall<any>({
      url: `/campaigns/${campaignId}`,
      method: "DELETE",
    }),

  getCampaigns: (organizationId: string) =>
    apiCall<any[]>({
      url: `/organizations/${organizationId}/campaigns`,
      method: "GET",
    }),

  getCampaign: (organizationId: string, campaignId: string) =>
    apiCall<any>({
      url: `/organizations/${organizationId}/campaigns/${campaignId}`,
      method: "GET",
    }),

  getCampaignStats: (organizationId: string, campaignId: string) =>
    apiCall<any>({
      url: `/organizations/${organizationId}/campaigns/${campaignId}/stats`,
      method: "GET",
    }),

  // This organization's enrollments, with progress (attempts, max_attempts, next_call_date).
  getCampaignEnrollments: (organizationId: string, campaignId: string) =>
    apiCall<CampaignEnrollmentResponse[]>({
      url: `/organizations/${organizationId}/campaigns/${campaignId}/enrollments`,
      method: "GET",
    }),

  // Get patient's campaign enrollments
  getPatientCampaigns: (
    patientId: string,
    options?: { status?: string; includeCompleted?: boolean }
  ) =>
    apiCall<
      {
        id: string;
        definition_id: string;
        organization_id: string;
        patient_id: string | null;
        enrolled_at: string | null;
        enrolled_by: string | null;
        status: string;
        context: Record<string, unknown>;
        paused_until: string | null;
        pause_reason: string | null;
        pause_start: string | null;
        pause_end: string | null;
        settings_overrides: Record<string, unknown>;
        completed_at: string | null;
        outcome: string | null;
        created_at: string;
        updated_at: string;
        name: string;
        max_attempts: number | null;
        attempts: number;
        next_call_date: string | null;
      }[]
    >({
      url: `/patients/${patientId}/campaigns`,
      method: "GET",
      params: {
        ...(options?.status && { status: options.status }),
        ...(options?.includeCompleted !== undefined && {
          include_completed: options.includeCompleted,
        }),
      },
    }),

  // Bulk campaign enrollment
  bulkEnrollPatients: (
    organizationId: string,
    campaignId: string,
    patientIds: string[],
    enrolledBy: string = "portal"
  ) =>
    apiCall<{
      successful: number;
      failed: number;
      already_enrolled: number;
      errors?: string[];
    }>({
      url: `/organizations/${organizationId}/campaigns/${campaignId}/enrollments/bulk`,
      method: "POST",
      data: patientIds,
      params: { enrolled_by: enrolledBy },
    }),

  // Bulk campaign unenrollment
  bulkUnenrollPatients: (
    organizationId: string,
    campaignId: string,
    patientIds: string[],
    reason?: string
  ) =>
    apiCall<{
      successful: number;
      failed: number;
      errors?: string[];
    }>({
      url: `/organizations/${organizationId}/campaigns/${campaignId}/enrollments/bulk`,
      method: "DELETE",
      data: patientIds,
      params: reason ? { reason } : undefined,
    }),

  // Single enrollment actions
  pauseEnrollment: (campaignId: string, enrollmentId: string) =>
    apiCall<{ message: string }>({
      url: `/campaigns/${campaignId}/enrollments/${enrollmentId}/pause`,
      method: "POST",
    }),

  resumeEnrollment: (campaignId: string, enrollmentId: string) =>
    apiCall<{ message: string }>({
      url: `/campaigns/${campaignId}/enrollments/${enrollmentId}/resume`,
      method: "POST",
    }),

  removeEnrollment: (campaignId: string, enrollmentId: string) =>
    apiCall<{ message: string }>({
      url: `/campaigns/${campaignId}/enrollments/${enrollmentId}`,
      method: "DELETE",
    }),

  // Pause campaign indefinitely (changes campaign status)
  pauseCampaign: (campaignId: string) =>
    apiCall<any>({
      url: `/campaigns/${campaignId}/pause`,
      method: "POST",
    }),

  // Resume a paused campaign
  resumeCampaign: (campaignId: string) =>
    apiCall<any>({
      url: `/campaigns/${campaignId}/resume`,
      method: "POST",
    }),

  // Postpone campaign (set pause window - calls scheduled within will be rescheduled)
  postponeCampaign: (
    campaignId: string,
    data: { pause_start: string; pause_end: string; reason?: string }
  ) =>
    apiCall<any>({
      url: `/campaigns/${campaignId}/postpone`,
      method: "POST",
      data,
    }),

  // Clear campaign postpone
  clearCampaignPostpone: (campaignId: string) =>
    apiCall<any>({
      url: `/campaigns/${campaignId}/postpone`,
      method: "DELETE",
    }),

  // Postpone specific enrollment (set pause window)
  postponeEnrollment: (
    campaignId: string,
    enrollmentId: string,
    data: { pause_start: string; pause_end: string; reason?: string }
  ) =>
    apiCall<any>({
      url: `/campaigns/${campaignId}/enrollments/${enrollmentId}/postpone`,
      method: "POST",
      data,
    }),

  // Clear enrollment postpone
  clearEnrollmentPostpone: (campaignId: string, enrollmentId: string) =>
    apiCall<any>({
      url: `/campaigns/${campaignId}/enrollments/${enrollmentId}/postpone`,
      method: "DELETE",
    }),

  // Bulk pause/resume campaigns for patients
  bulkUpdateCampaignStatus: (
    organizationId: string,
    patientIds: string[],
    action: "pause" | "resume",
    campaignId?: string,
    pauseReason?: string,
    pauseDuration?: "1_week" | "2_weeks" | "1_month" | null
  ) =>
    apiCall<{
      updated: number;
      errors?: string[];
    }>({
      url: "/patient/bulk/campaign-status",
      method: "PATCH",
      data: patientIds,
      params: {
        organization_id: organizationId,
        action,
        ...(campaignId && { campaign_id: campaignId }),
        ...(pauseReason && { pause_reason: pauseReason }),
        ...(pauseDuration && { pause_duration: pauseDuration }),
      },
    }),

  // Bulk update consent status
  bulkUpdateConsentStatus: (
    organizationId: string,
    patientIds: string[],
    consentStatus: "CONSENTED" | "DECLINED" | "UNCERTAIN" | "NOT_ASKED",
    methodOfConsent?: string
  ) =>
    apiCall<{
      updated: number;
      errors?: string[];
    }>({
      url: "/patient/bulk/consent-status",
      method: "PATCH",
      data: patientIds,
      params: {
        organization_id: organizationId,
        consent_status: consentStatus,
        ...(methodOfConsent && { method_of_consent: methodOfConsent }),
      },
    }),

  // Update patient organization (for marking inactive)
  updatePatientOrganization: (data: {
    patient_id: string;
    organization_id: string;
    is_active?: boolean;
  }) =>
    apiCall<any>({
      url: "/patient/organization",
      method: "POST",
      data,
    }),

  queryCallsV3: (
    organizationId: string,
    params: CallFilterResult & {
      limit?: number;
      offset?: number;
      sort_by?: string;
      sort_order?: string;
    }
  ) => {
    const sanitizedParams = { ...params };

    // Fix JSON encoding issue - ensure no spaces in JSON strings
    if (sanitizedParams.call_outcome_values) {
      try {
        const parsed = JSON.parse(sanitizedParams.call_outcome_values);
        sanitizedParams.call_outcome_values = JSON.stringify(parsed);
      } catch (e) {
        console.warn(
          "Invalid JSON in call_outcome_values:",
          sanitizedParams.call_outcome_values
        );
      }
    }

    // Fix strategy_types case - backend expects uppercase
    if (sanitizedParams.strategy_types) {
      // Backend expects uppercase values for strategy types
      sanitizedParams.strategy_types = sanitizedParams.strategy_types
        .split(",")
        .map((type) => type.trim().toUpperCase())
        .join(",");
    }

    return apiCall<CallQueryResults>({
      url: "/call/query",
      method: "GET",
      params: {
        ...sanitizedParams,
        organization_id: organizationId,
      },
    });
  },
};

export default api;

export interface WorkflowRoleAssignment { role_key:string; user_id:string|null; revision:number; updated_by:string; updated_at:string; assignee_active:boolean|null }
